import React, { useCallback, useContext, useMemo, useState } from 'react';
import DownloadIcon from '@mui/icons-material//CloudDownload';
import DeleteIcon from '@mui/icons-material//Delete';
import TimelineIcon from '@mui/icons-material//Timeline';
import { Box, Button, Menu, MenuItem, styled, Typography } from '@mui/material';
import clsx from 'clsx';
import { useStore } from 'common/store';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import { useDocuments } from 'services/hooks';
import theme from 'theme';
import SendFileDialog from './SendFileDialog';
import { ActionMenuProps, CurrentRequestedDocumentTableMultipleSelectedBoxProps } from './types';

const PREFIX = 'CurrentRequestedDocumentTableMultipleSelectedBox';

const classes = {
  fadeIn: `${PREFIX}-fadeIn`,
  arrowBox: `${PREFIX}-arrowBox`,
  whiteText: `${PREFIX}-whiteText`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')({
  [`& .${classes.fadeIn}`]: {
    animation: 'fadeIn 0.5s',
  },
  [`& .${classes.arrowBox}`]: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '100%',
      left: '2%',
      marginLeft: '-10px',
      borderWidth: '10px',
      borderStyle: 'solid',
      borderColor: `${theme.palette.primary.main} transparent transparent transparent`,
    },
  },
  [`& .${classes.whiteText}`]: {
    color: theme.palette?.common?.white ?? 'inherit',
  },
});

const ActionMenu = ({
  anchorEl,
  handleClose,
  handleDownloadAll,
  handleDeleteAll,
  handleMoveAll,
  permissions,
}: ActionMenuProps) => (
  <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
    <MenuItem onClick={handleDownloadAll}>
      <DownloadIcon style={{ marginRight: theme.spacing(1) }} />
      Download selected
    </MenuItem>
    {permissions?.canUseInfoRequestLedger && (
      <Root>
        <MenuItem onClick={handleDeleteAll}>
          <DeleteIcon style={{ marginRight: theme.spacing(1) }} />
          Delete selected
        </MenuItem>
        <MenuItem onClick={handleMoveAll}>
          <TimelineIcon style={{ marginRight: theme.spacing(1) }} />
          Send to another measurement date
        </MenuItem>
      </Root>
    )}
  </Menu>
);

const CurrentRequestedDocumentTableMultipleSelectedBox = ({
  selectedFiles,
  setSelectedFiles,
}: CurrentRequestedDocumentTableMultipleSelectedBoxProps) => {
  const [isSendFileDialogOpen, setIsSendFileDialogOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [{ firmId }] = useStore();
  const { downloadMultipleDocuments, deleteMultipleFiles } = useDocuments();
  const { refreshDocuments, measurementDates, currentMeasurementDateId, permissions } = useContext(
    InformationDashboard409AContext
  );

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDownloadAll = useCallback(async () => {
    await downloadMultipleDocuments(selectedFiles, firmId ?? '');
    handleClose();
  }, [downloadMultipleDocuments, selectedFiles, firmId, handleClose]);

  const handleDeleteAll = useCallback(async () => {
    await deleteMultipleFiles(selectedFiles);
    setSelectedFiles([]);
    refreshDocuments();
    handleClose();
  }, [deleteMultipleFiles, selectedFiles, setSelectedFiles, refreshDocuments, handleClose]);

  const handleMoveAll = useCallback(() => {
    setIsSendFileDialogOpen(true);
    handleClose();
  }, [handleClose]);

  const closeSendFileDialog = useCallback(() => {
    setIsSendFileDialogOpen(false);
  }, []);

  const otherMeasurementDates = useMemo(
    () => measurementDates?.filter(date => date.id !== currentMeasurementDateId),
    [measurementDates, currentMeasurementDateId]
  );

  return (
    <>
      {otherMeasurementDates && (
        <SendFileDialog
          open={isSendFileDialogOpen}
          onClose={closeSendFileDialog}
          files={selectedFiles}
          measurementDates={otherMeasurementDates}
        />
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2}
        borderRadius={4}
        boxShadow={2}
        className={clsx(classes.fadeIn, classes.arrowBox)}
        style={{
          backgroundColor: theme.palette.primary.main,
        }}>
        <Typography
          variant="h6"
          className={classes.whiteText}
          style={{
            paddingLeft: theme.spacing(2),
          }}>
          {selectedFiles.length} documents selected
        </Typography>
        <div>
          <Button variant="contained" color="secondary" onClick={handleClick}>
            Actions
          </Button>
          <ActionMenu
            anchorEl={anchorEl}
            handleClose={handleClose}
            handleDownloadAll={handleDownloadAll}
            handleDeleteAll={handleDeleteAll}
            handleMoveAll={handleMoveAll}
            permissions={permissions}
          />
        </div>
      </Box>
    </>
  );
};

export default CurrentRequestedDocumentTableMultipleSelectedBox;
